import { startCashfreePayment, supportedWebGateways } from './cashfreePayment';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import HomeDashboardLayout from '../HomeDashboardLayout';
import HeaderProfileSection from '../HeaderProfileSection';
import PaymentDetail from './PaymentDetail';
import PaymentMethodd from './PaymentMethodd';
import { SUBSCRIPTION_ROUTES } from './subscriptionRoutes';
import { GAMIFICATION_ROUTES } from '../gamification/gamificationRoutes';
import { applyCoinCoupon, applyExistingCoins, createRazorpayCoinOrder,
  fetchAvailableCoins, fetchCoinOrderAmounts, fetchCoinPaymentGateways, removeCoinCoupon,
  removeExistingCoins } from '../../services/coinPurchaseService';
import '../../dashboard/Dashboard.css';
import '../HomeDashboard.css';
import '../gamification/Coinstore.css';
import '../../questionBank/QuestionBankList.css';
import './SelectPaymentMethod.css';

function loadRazorpay() {
  if (window.Razorpay) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Unable to load Razorpay checkout.'));
    document.body.appendChild(script);
  });
}

const getError = (error) => error?.response?.data?.message || error?.message || 'Unable to update the order.';

export default function SelectPaymentMethod() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isRapid = Boolean(location.state?.rapidFirePlan);
  const isAssessment = Boolean(location.state?.reattemptPack);
  const purchaseType = isRapid ? 'rapid' : isAssessment ? 'assessment' : 'coins';
  const pack = isRapid ? location.state.rapidFirePlan : isAssessment ? location.state.reattemptPack : location.state?.coinPack;
  const suppliedOrder = isRapid ? location.state?.rapidFireOrder : isAssessment ? location.state?.assessmentOrder : location.state?.coinOrder;
  const order = suppliedOrder?.vk_order_id ? suppliedOrder : null;
  const storePath = isRapid ? SUBSCRIPTION_ROUTES.rapidFireAttempts
    : isAssessment ? SUBSCRIPTION_ROUTES.assessmentReattempts
    : location.state?.coinStorePath === GAMIFICATION_ROUTES.coinStore
      ? GAMIFICATION_ROUTES.coinStore : SUBSCRIPTION_ROUTES.coinStore;
  const [gateways, setGateways] = useState(() => supportedWebGateways(location.state?.paymentGateways || []));
  const [selectedGateway, setSelectedGateway] = useState(() => gateways[0] || null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [payable, setPayable] = useState(order ? Number(order.amount) : null);
  const [availableCoins, setAvailableCoins] = useState(null);
  const [conversionLabel, setConversionLabel] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState('');
  const [coins, setCoins] = useState('');
  const [appliedCoins, setAppliedCoins] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const actionBusy = useRef(false);

  useEffect(() => {
    if (!order?.vk_order_id) return;
    let active = true;
    const loadOrderDetails = async () => {
      try {
        const [amounts, coinData] = await Promise.all([
          fetchCoinOrderAmounts(order.vk_order_id), fetchAvailableCoins(),
        ]);
        if (!active) return;
        if (amounts.final_amount != null) setPayable(Number(amounts.final_amount));
        setAvailableCoins(Number(coinData.available_coins ?? coinData.coins ?? coinData.total_coins ?? 0));
        if (coinData.conversion_label) setConversionLabel(coinData.conversion_label);
      } catch (loadFailure) {
        if (active) setError(getError(loadFailure));
      }
    };
    loadOrderDetails();
    return () => { active = false; };
  }, [order?.vk_order_id]);

  const refreshAmount = async (orderId) => {
    const amounts = await fetchCoinOrderAmounts(orderId);
    const amount = Number(amounts.final_amount);
    if (!Number.isFinite(amount)) throw new Error('The server did not return a payable amount.');
    setPayable(amount);
    return { ...amounts, final_amount: amount };
  };

  const runChange = async (request, afterChange) => {
    if (!order?.vk_order_id || actionBusy.current) return;
    actionBusy.current = true;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const result = await request();
      const changes = await Promise.allSettled([afterChange(result), refreshAmount(order.vk_order_id)]);
      const failure = changes.find((change) => change.status === 'rejected');
      if (failure) throw failure.reason;
      setMessage(result.message || 'Order updated.');
    } catch (changeFailure) {
      setError(getError(changeFailure));
    } finally {
      actionBusy.current = false;
      setBusy(false);
    }
  };

  const handleCoupon = () => runChange(
    () => appliedCoupon ? removeCoinCoupon(order.vk_order_id) : applyCoinCoupon(order.vk_order_id, couponCode.trim()),
    async (result) => {
      const nextCoupon = appliedCoupon ? '' : String(result.coupon_code || couponCode.trim());
      setSelectedGateway(null);
      setGateways([]);
      setAppliedCoupon(nextCoupon);
      setCouponCode(nextCoupon);
      setCouponDiscount(appliedCoupon ? 0 : Number(result.discount_amount || 0));
      const nextGateways = await fetchCoinPaymentGateways(nextCoupon);
      const supported = supportedWebGateways(nextGateways);
      setGateways(supported);
      setSelectedGateway(supported[0] || null);
    }
  );

  const handleCoins = () => runChange(
    () => appliedCoins ? removeExistingCoins(order.vk_order_id) : applyExistingCoins(order.vk_order_id, coins),
    async () => {
      setAppliedCoins(appliedCoins ? '' : coins);
      if (appliedCoins) setCoins('');
    }
  );

  const handlePay = async () => {
    if (!order?.vk_order_id || !selectedGateway || actionBusy.current) return;
    actionBusy.current = true;
    setBusy(true);
    setError('');
    try {
      const amountInfo = await refreshAmount(order.vk_order_id);
      if (amountInfo.final_amount <= 0) throw new Error('This order has no payable balance.');
      if (!gateways.some((item) => item.payment_gateway_id === selectedGateway.payment_gateway_id)) {
        throw new Error('Please select an available payment method.');
      }
      if (['bank-transfer', 'upi-transfer'].includes(selectedGateway.slug)) {
        actionBusy.current = false;
        setBusy(false);
        navigate(SUBSCRIPTION_ROUTES.manualTransfer, {
          state: { ...(isRapid ? { rapidFirePlan: pack, rapidFireOrder: order } : isAssessment ? { reattemptPack: pack, assessmentOrder: order } : { coinPack: pack, coinOrder: order,
            coinStorePath: storePath }), paymentGateway: selectedGateway },
        });
        return;
      }
      if (selectedGateway.slug === 'cashfree') {
        await startCashfreePayment({ orderId: order.vk_order_id, gateway: selectedGateway,
          pendingState: { vkOrderId: order.vk_order_id, storePath, purchaseType },
          navigate, amount: amountInfo.final_amount });
        return;
      }
      if (selectedGateway.slug !== 'razorpay') throw new Error('This payment method is unavailable on web.');
      if (!selectedGateway.client_id) throw new Error('Razorpay checkout is unavailable.');
      await loadRazorpay();
      const gatewayOrder = await createRazorpayCoinOrder(order.vk_order_id, selectedGateway.payment_gateway_id);
      const pendingState = { vkOrderId: order.vk_order_id, storePath, purchaseType };
      sessionStorage.setItem('subscriptionPurchasePending', JSON.stringify(pendingState));
      const checkout = new window.Razorpay({
        key: selectedGateway.client_id,
        amount: Math.round(amountInfo.final_amount * 100),
        currency: order.currency || 'INR',
        order_id: gatewayOrder.razorpay_order_id,
        name: 'Vidyakasam',
        description: isRapid ? `Purchase ${pack.attempt_amount} rapid fire attempts` : isAssessment ? `Purchase ${pack.attempt_amount || pack.amount || pack.coins} assessment attempts` : `Purchase ${pack.coins} coins`,
        prefill: {
          contact: user?.phone || user?.mobile || user?.phone_number || '',
          email: user?.email || '',
        },
        handler: (response) => navigate(SUBSCRIPTION_ROUTES.paymentResult, {
          state: { ...pendingState, paymentId: response.razorpay_payment_id },
        }),
        modal: { ondismiss: () => { actionBusy.current = false; setBusy(false); } },
      });
      checkout.on('payment.failed', (failure) => {
        actionBusy.current = false;
        setBusy(false);
        setError(failure?.error?.description || 'Payment failed. Please try again.');
      });
      checkout.open();
    } catch (paymentFailure) {
      actionBusy.current = false;
      setBusy(false);
      setError(getError(paymentFailure));
    }
  };

  return (
    <>
      <style>{`.homeDashboard-wrapper .page-content { padding-right: 0; }
        .homeDashboard-wrapper .main-content { height: 100vh; overflow: hidden; box-sizing: border-box; padding-bottom: 35px; min-height: 0; }`}</style>
      <HomeDashboardLayout showHeader={false}>
      <div className="qbl-page qbankList-page page-padding start-gamification coin-payment-page">
        <div className="start-quiz qbl-main instruction-main">
          <div className="hideProfile-page"><div className="categories-page-header mb-0">
            <div><button type="button" className="categories-back-button" onClick={() => navigate(storePath)} aria-label="Go back"><i className="bi bi-chevron-left" /></button></div>
            <div><h4 className="d-flex categories-page-title"> </h4></div><div><HeaderProfileSection /></div>
          </div></div>
          <div className="coine-balance-wrapper">
            <div className="rapid-fire-header"><h1 className="qb-title">Payment <span>Method</span></h1></div>
            <p className="game-description">Complete, Learn &amp; improve Daily</p>
            {!pack || !order ? <p role="alert">Select a {isRapid ? 'rapid attempt plan' : isAssessment ? 'assessment reattempt plan' : 'coin pack'} to create an order before continuing.</p> : (
              <div className="detail-main-box coin-payment-columns">
                <section className="qbank-LeftContent">
                  <PaymentDetail pack={pack} purchaseType={purchaseType} order={{ ...order, coupon_discount: couponDiscount }} payable={payable} couponCode={couponCode} setCouponCode={setCouponCode} appliedCoupon={appliedCoupon} coins={coins} setCoins={setCoins} appliedCoins={appliedCoins} availableCoins={availableCoins} conversionLabel={conversionLabel} busy={busy} onCoupon={handleCoupon} onCoins={handleCoins} error={error} message={message} />
                </section>
                <aside><PaymentMethodd gateways={gateways} selectedGateway={selectedGateway} onSelect={setSelectedGateway} onPay={handlePay} busy={busy} payable={payable} ready={Boolean(order?.vk_order_id)} /></aside>
              </div>
            )}
          </div>
        </div>
      </div>
    </HomeDashboardLayout>
    </>
  );
}
